import React, { useState, useEffect, useMemo, useRef  } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Box, Typography, Breadcrumbs, Link as MuiLink, Container,
  Stack, Divider, Chip, InputBase, CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, Clock, IndianRupee, Check, Star, ShieldCheck,
  Cloud, Wrench, Server, HardDrive, Network, Laptop, Search, Building2,
  Users, Package,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PageShell from "../components/layout/PageShell";
import { OVERVIEW_ITEMS } from "../data/businessOverview"; 
import ContactSection from "../components/shared/ContactSection";
import {
  getBusinessPageContent, getBusinessOfferings,
  getProducts, getProductCategories,
  getPartners, getFeaturedPartners,
} from "../api/business";
import LogoMarquee from "../components/ui/LogoMarquee";
import heroBg from "./bussinessbackground.png"; // adjust path/filename to your actual asset
/* ------------------------------------------------------------------ */
/*  Shared visual helpers                                             */
/* ------------------------------------------------------------------ */

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
});

// Faint blueprint grid, identical treatment to BusinessItems / ServicesGrid / CaseStudies
function BlueprintBackdrop() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage:
          "linear-gradient(rgba(29,78,216,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(29,78,216,0.045) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 78%)",
        WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 0%, transparent 78%)",
      }}
    />
  );
}

function Eyebrow({ children }) {
  return (
    <Box
      sx={{
        display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.75, py: 0.6,
        borderRadius: 999, border: "1px solid rgba(37,99,235,0.18)",
        bgcolor: "rgba(37,99,235,0.05)", mb: 2.25,
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#2563EB" }} />
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.1em", color: "#1D4ED8", textTransform: "uppercase" }}>
        {children}
      </Typography>
    </Box>
  );
}

// Renders initials if no logo image / the image 404s — never shows a broken-image icon
function InitialsBadge({ name, size = 44, tone = "#2563EB" }) {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <Box
      sx={{
        width: size, height: size, borderRadius: "12px", bgcolor: `${tone}14`, color: tone,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 800, fontSize: size * 0.36, letterSpacing: "-0.01em", flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
}

// height bumped 28 → 36 so logos read clearly in the compact partner strip
function BrandLogo({ src, name, height = 36 }) {
  const [failed, setFailed] = useState(!src);
  if (failed) return <InitialsBadge name={name} size={height + 8} />;
  return (
    <Box
      component="img" src={src} alt={name} onError={() => setFailed(true)}
      sx={{ height, width: "auto", maxWidth: 140, objectFit: "contain" }}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Unified card system                                                */
/*  Every card in this page (overview, services, products, partners)  */
/*  is built from this one component so sizing, truncation, and       */
/*  hover behaviour are perfectly consistent across the whole page.   */
/* ------------------------------------------------------------------ */

const CARD_HEIGHT = 360;
const CARD_MEDIA_HEIGHT = 190;

// Top area of every card: a real photo if we have one, otherwise a
// soft tinted panel with a representative icon — same footprint either way.
//
// `fit="cover"` is for photographic content (product shots) that should
// fill the frame edge-to-edge. `fit="contain"` is for logos/wordmarks —
// these render at their natural aspect ratio, padded, on a flat panel,
// so a small square mark or a wide wordmark never gets stretched, cropped
// or blurred out trying to fill a 16:9 box.
//
// Padding for the "contain" (logo) variant was reduced from a heavy 28px
// (p: 3.5) down to 12px (p: 1.5), and the panel is now pure white instead
// of tinted blue — together this lets each logo occupy noticeably more of
// the card's media area and reads as a clean, professional "logo plate"
// rather than a small icon lost in a large tinted box.
function CardMedia({ src, alt, icon: Icon = Package, tone = "#2563EB", fit = "cover" }) {
  const [failed, setFailed] = useState(!src);
  const isContain = fit === "contain";
  return (
    <Box
      className={isContain ? "card-media card-media--contain" : "card-media card-media--cover"}
      sx={{
        height: CARD_MEDIA_HEIGHT,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        bgcolor: isContain ? "#FFFFFF" : "#F1F5FB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(isContain && { p: 1.5, borderBottom: "1px solid rgba(15,23,42,0.06)" }),
      }}
    >
      {!failed && src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          sx={{
            ...(isContain
              ? { maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }
              : { width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease" }),
          }}
        />
      ) : (
        <Icon size={32} color="#94A3B8" strokeWidth={1.5} />
      )}
    </Box>
  );
}

/**
 * ArticleCard — the single card design used everywhere on this page.
 *
 * Every card renders at the exact same width (governed by its CardGrid column)
 * and the exact same height (CARD_HEIGHT), regardless of content length.
 * Titles clamp to 2 lines and descriptions clamp to 2–3 lines with an
 * ellipsis, so no card ever grows to fit long copy. Styling stays quiet —
 * a hairline border, a soft shadow, and a small lift on hover — so the
 * photo and the copy do the work rather than the chrome around them.
 */
function ArticleCard({
  image,
  imageFit = "cover",
  icon,
  eyebrowIcon: EyebrowIcon,
  eyebrowLabel,
  title,
  description,
  meta,
  delay = 0,
  href, // if set, whole card is a link that opens in a new tab
}) {
  return (
    <motion.div {...fadeUp(delay)} style={{ height: "100%" }}>
      <Box
        {...(href ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" } : {})}
        sx={{
          height: CARD_HEIGHT,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "12px",
          border: "1px solid rgba(15,23,42,0.09)",
          bgcolor: "white",
          overflow: "hidden",
          textDecoration: "none",
          color: "inherit",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          cursor: href ? "pointer" : "default",
          transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: "0 16px 32px -20px rgba(15,23,42,0.22)",
            borderColor: "rgba(15,23,42,0.13)",
          },
          "&:hover .card-media--cover img": { transform: "scale(1.04)" },
          "&:hover .card-title": { color: "#2563EB", textDecoration: "underline", textDecorationColor: "rgba(37,99,235,0.35)", textUnderlineOffset: "3px" },
          "&:hover .card-arrow": { transform: "translateX(3px)", color: "#2563EB" },
        }}
      >
        <CardMedia src={image} alt={title} icon={icon} fit={imageFit} />

        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          {/* quiet accent rail marking the title/description column */}
          {/* <Box sx={{ width: 3, flexShrink: 0, bgcolor: "#2563EB", my: 2.25 }} /> */}

          <Box sx={{ pl: 2, pr: 2.25, py: 2.25, display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            {eyebrowLabel && (
              <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 1 }}>
                {EyebrowIcon && <EyebrowIcon size={13} color="#64748B" strokeWidth={2} />}
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#64748B" }}>
                  {eyebrowLabel}
                </Typography>
              </Stack>
            )}

            {/* Fix for clipped (non-ellipsized) text */}
            <Typography
              className="card-title"
              sx={{
                fontWeight: 700, fontSize: "0.98rem", color: "#0B1530", lineHeight: 1.35, mb: description ? 0.75 : 0,
                transition: "color 0.2s ease",
                minHeight: "calc(1.35em * 2)",
                flexShrink: 0,
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
              }}
            >
              {title}
            </Typography>

            {description && (
              <Typography
                sx={{
                  fontSize: 13, color: "#64748B", lineHeight: 1.6, mb: 1.5,
                  minHeight: "calc(1.6em * 2)",
                  flexShrink: 0,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                }}
              >
                {description}
              </Typography>
            )}

            <Box sx={{ mt: "auto" }}>
              {meta && <Box sx={{ mb: 1.25 }}>{meta}</Box>}
              <ArrowRight
                size={17}
                className="card-arrow"
                color="#334155"
                strokeWidth={2}
                style={{ transition: "transform 0.2s ease, color 0.2s ease" }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
}

/**
 * CardGrid — lays out ArticleCards on a plain CSS grid instead of MUI's
 * Grid. MUI's xs/sm/md breakpoints key off the browser *viewport*, not the
 * width the grid actually has to work with — so if this page ever renders
 * inside a narrower shell (a sidebar layout, an embedded preview, etc.)
 * the breakpoints and the real available space fall out of sync, and you
 * get lopsided rows or single full-width cards even on a wide screen. A
 * CSS grid with `minmax` sizes columns off the container itself, so every
 * card is always the same width and the column count always matches the
 * space that's actually there.
 *
 * `auto-fill` (not `auto-fit`) is deliberate: `auto-fit` collapses any
 * empty tracks and lets the remaining `1fr` track(s) expand to fill the
 * entire row — so when a filter leaves exactly one matching card, that
 * card would stretch across the full grid width, badly distorting its
 * `cover`-fit image. `auto-fill` keeps the empty tracks in place, so a
 * lone result keeps its normal card width instead of ballooning.
 * The `maxWidth` on grid children is a second safety net for the same
 * scenario on very narrow containers.
 *
 * `justifyContent: "center"` is added purely for horizontal centering:
 * when the grid's tracks don't fill the full container width (e.g. a
 * search/filter leaves only 2–3 cards on a row that could fit 4), CSS
 * grid centers the occupied tracks as a group instead of leaving them
 * pinned to the left edge. This doesn't affect column sizing, wrapping,
 * or the `auto-fill` behaviour described above — it's purely alignment.
 */
function CardGrid({ minWidth = 260, children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
        gap: 3,
        alignItems: "stretch",
        justifyContent: "center",
        "& > *": { maxWidth: 380 },
      }}
    >
      {children}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Small helper: normalize either a raw array or a paginated          */
/*  { results, total, ... } response into a plain array.               */
/* ------------------------------------------------------------------ */
function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}


const OVERVIEW_ICONS = [Sparkles, Check, Star, ShieldCheck];

// const STATIC_OVERVIEW_CARDS =[
//   {
//     slug: "business-scope",
//     title: "Business Scope",
//     image: "/images/overview/business-scope.jpg",
//     description:
//       "Services for SMEs to large global enterprises — IT services, consultancy, and hardware/software products, tailor-made to combine industry knowledge with client needs.",
//     detailed_description:
//       "Write the full, longer-form static copy for this page here. This can be a few paragraphs — it renders on the detail page the same way `product.detailed_description` does on ProductDetailPage.",
//   },
//   {
//     slug: "workplace-culture",
//     title: "Workplace Culture",
//     image: "/images/overview/workplace-culture.jpg",
//     description:
//       "A commitment to continuous skills development and training fosters a productive, vibrant and growing workforce, aligning employee growth with technological advancement.",
//     detailed_description:
//       "Full static detail copy for Workplace Culture goes here.",
//   },
//   {
//     slug: "our-purpose",
//     title: "Our Purpose",
//     image: "/images/overview/our-purpose.jpg",
//     description:
//       "A leader in the IT industry with unique, customized services — focused on long-term client relationships, delivering value, and empowering businesses through innovation.",
//     detailed_description:
//       "Full static detail copy for Our Purpose goes here.",
//   },
// ];


/* ------------------------------------------------------------------ */
/*  Tab switcher — pill segmented control                              */
/* ------------------------------------------------------------------ */

const TAB_META = [
  { value: "overview", label: "Business Overview" },
  { value: "it-services", label: "IT Services & Consultancy" },
  { value: "hardware-software", label: "Hardware & Software" },
  { value: "clients-partners", label: "Our Clients & Partners" },
];

function TabSwitcher({ active, onChange, containerRef }) {
  return (
    <Box 
    ref={containerRef}
    sx={{ bgcolor: "#F8FAFC", borderBottom: "1px solid rgba(15,23,42,0.06)", position: "sticky", top: 84, zIndex: 10 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          py: 1.75,
          px: { xs: 2, md: 0 },              // ← edge padding so chips aren't clipped by viewport
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",   // ← smooth momentum scroll on iOS
          scrollSnapType: "x proximity",      // ← chips settle into view instead of stopping mid-cut
          justifyContent: { xs: "flex-start", md: "center" }, // ← left-align on mobile, don't force-center into overflow
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {TAB_META.map(({ value, label }) => {
          const isActive = active === value;
          return (
            <Box
              key={value}
              onClick={() => onChange(value)}
              sx={{
                cursor: "pointer",
                whiteSpace: "nowrap",
                px: 2.25,
                py: 1,
                borderRadius: "999px",
                fontSize: 13.75,
                fontWeight: 700,
                letterSpacing: "-0.005em",
                flexShrink: 0,
                scrollSnapAlign: "start",       // ← pairs with scrollSnapType above
                color: isActive ? "white" : "#475569",
                bgcolor: isActive ? "#2563EB" : "transparent",
                border: "1px solid",
                borderColor: isActive ? "#2563EB" : "rgba(15,23,42,0.10)",
                boxShadow: isActive ? "0 8px 20px -10px rgba(37,99,235,0.55)" : "none",
                transition: "all 0.2s cubic-bezier(0.22,1,0.36,1)",
                "&:hover": !isActive ? { borderColor: "rgba(37,99,235,0.4)", color: "#1D4ED8" } : {},
              }}
            >
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab content sections — DYNAMIC versions                            */
/* ------------------------------------------------------------------ */

function OverviewSection() {
  const { data: pageContent, isLoading } = useQuery({
    queryKey: ["business-page-content"],
    queryFn: () => getBusinessPageContent().then((r) => r.data),
  });

const overviewCards = useMemo(() => {
  const cards = normalizeList(pageContent?.scope_cards);
  if (cards.length === 0) return OVERVIEW_ITEMS;
  return cards.map((c) => ({
    slug: c.slug,
    title: c.title || c.name,
    description: c.description,
    image: c.image || c.icon_or_image,
  }));
}, [pageContent]);

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <motion.div {...fadeUp(0)}>
        <Box sx={{ textAlign: "center" }}>
          <Eyebrow>About the company</Eyebrow>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.7rem", md: "2.1rem" }, color: "#0B1530", letterSpacing: "-0.02em", mb: 1.5 }}>
            {pageContent?.hero_title || "Our Business"}
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.75, maxWidth: 640, mx: "auto", mb: 5 }}>
            {pageContent?.hero_subtitle ||
              "NetLabs ITS is a full-service IT partner — consultancy, managed services, hardware and software supply, delivered for organizations from growing SMEs to large enterprises."}
          </Typography>
        </Box>
      </motion.div>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} /></Box>
      ) : overviewCards.length === 0 ? null : (
       <CardGrid minWidth={260}>
 {overviewCards.map(({ title, image, description, slug }, i) => (
  <ArticleCard
    key={slug || title}
    image={image}
    imageFit="cover"
    icon={Package}
    title={title}
    description={description}
    eyebrowIcon={Sparkles}
    eyebrowLabel="About us"
    delay={i * 0.08}
    href={`/business/overview/${slug}`}
  />
))}
</CardGrid>
      )}

      <Box sx={{ mt: 7 }}>
        <PartnersStrip variant="compact" />
      </Box>
    </Box>
  );
}

function OfferingsGrid({ category, eyebrowIcon, eyebrowLabel }) {
  const { data, isLoading } = useQuery({
    queryKey: ["business-offerings", category],
    queryFn: () => getBusinessOfferings({ category }).then((r) => normalizeList(r.data)),
  });

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} /></Box>;
  }
  if (!data || data.length === 0) {
    return <Typography sx={{ color: "text.secondary", fontSize: 14, textAlign: "center" }}>No entries yet.</Typography>;
  }

  return (
    <CardGrid minWidth={240}>
     {data.map((offering, i) => (
       <ArticleCard
          key={offering.slug || offering.id}
          image={offering.icon_or_image}
          imageFit="contain"
          icon={Package}
          title={offering.title}
          description={offering.short_description}
          eyebrowIcon={eyebrowIcon}
          eyebrowLabel={eyebrowLabel}
          delay={i * 0.06}
          href={`/business/services/${offering.slug}`}
        />
      ))}
    </CardGrid>
  );
}

function ServicesSection() {
  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <motion.div {...fadeUp(0)}>
        <Box sx={{ textAlign: "center" }}>
          <Eyebrow>Services portfolio</Eyebrow>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.7rem", md: "2.1rem" }, color: "#0B1530", letterSpacing: "-0.02em", mb: 1.5 }}>
            IT Services &amp; Consultancy
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.75, maxWidth: 640, mx: "auto", mb: 5 }}>
            Comprehensive IT consultancy and support for businesses of every size.
          </Typography>
        </Box>
      </motion.div>

      <OfferingsGrid category="SERVICE" eyebrowIcon={ShieldCheck} eyebrowLabel="Service" />

      <motion.div {...fadeUp(0)}>
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.4rem", md: "1.65rem" }, color: "#0B1530", letterSpacing: "-0.02em", mt: 7, mb: 1.25 }}>
            Our Managed Services
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#475569", lineHeight: 1.7, maxWidth: 640, mx: "auto", mb: 4 }}>
            Managed services are the practice of outsourcing the day-to-day management responsibilities and
            functions that are critical to any successful business.
          </Typography>
        </Box>
      </motion.div>

      <OfferingsGrid category="BUSINESS" eyebrowIcon={Wrench} eyebrowLabel="Managed service" />
    </Box>
  );
}

function CatalogSection() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [query, setQuery] = useState("");

  const { data: categoriesRaw = [] } = useQuery({
    queryKey: ["product-categories"],
    queryFn: () => getProductCategories().then((r) => normalizeList(r.data)),
  });
  const categoryChips = [{ slug: "All", name: "All" }, ...categoriesRaw];

  const { data: productsRaw, isLoading } = useQuery({
    queryKey: ["products", activeCategory, query],
    queryFn: () =>
      getProducts({
        category: activeCategory === "All" ? undefined : activeCategory,
        search: query || undefined,
      }).then((r) => normalizeList(r.data)),
  });
  const filtered = productsRaw || [];

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <motion.div {...fadeUp(0)}>
        <Box sx={{ textAlign: "center" }}>
          <Eyebrow>Product catalog</Eyebrow>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.7rem", md: "2.1rem" }, color: "#0B1530", letterSpacing: "-0.02em", mb: 1.5 }}>
            Hardware &amp; Software Products
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.75, maxWidth: 640, mx: "auto", mb: 4 }}>
            Browse, compare, and find the right solution from our catalog.
          </Typography>
        </Box>
      </motion.div>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 4, justifyContent: "center" }} alignItems="center">
        <Box sx={{
          display: "flex", alignItems: "center", gap: 1, bgcolor: "white", border: "1px solid rgba(15,23,42,0.10)",
          borderRadius: "10px", px: 1.75, py: 1, width: { xs: "100%", sm: 340 },
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:focus-within": { borderColor: "#2563EB", boxShadow: "0 0 0 3px rgba(37,99,235,0.12)" },
        }}>
          <Search size={16} color="#94A3B8" />
          <InputBase
            placeholder="Search products…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ fontSize: 13.75, flex: 1 }}
          />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1 }}>
          {categoryChips.map((cat) => {
            const value = cat.slug ?? cat.id ?? cat.name;
            const isActive = activeCategory === value;
            return (
              <Chip
                key={value}
                label={cat.name}
                onClick={() => setActiveCategory(value)}
                sx={{
                  fontWeight: 700, fontSize: 13, borderRadius: "999px", cursor: "pointer",
                  transition: "all 0.2s ease",
                  bgcolor: isActive ? "#2563EB" : "rgba(37,99,235,0.06)",
                  color: isActive ? "white" : "#2563EB",
                  "&:hover": { bgcolor: isActive ? "#1D4ED8" : "rgba(37,99,235,0.12)" },
                }}
              />
            );
          })}
        </Box>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} /></Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography fontWeight={700} color="#334155">No products match your search</Typography>
        </Box>
      ) : (
      <CardGrid minWidth={240}>
          {filtered.map((product, i) => (
            <ArticleCard
              key={product.slug || product.id}
              image={product.image}
              icon={Package}
              title={product.name}
              description={product.short_description}
              eyebrowLabel={product.vendor?.name}
              delay={i * 0.05}
              href={`/products/${product.slug}`}
              meta={
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                  {product.category?.name && (
                    <Chip
                      label={product.category.name}
                      size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB" }}
                    />
                  )}
                  {product.product_type && (
                    <Chip
                      label={product.product_type === "HARDWARE" ? "Hardware" : "Software"}
                      size="small"
                      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(15,23,42,0.05)", color: "#475569" }}
                    />
                  )}
                </Stack>
              }
            />
          ))}
        </CardGrid>
      )}
    </Box>
  );
}

const accreditations = [
  { name: "NAICU", src: "/logo/NAICU.png" },
  { name: "PMP", src: "/logo/PMP.png" },
  { name: "AAHEA", src: "/logo/AAHEA.png" },
  { name: "GARP", src: "/logo/GARP.jpg" },
  { name: "The Agile Alliance", src: "/logo/AgileAlliance.jpg" },
  { name: "Six Sigma Council", src: "/logo/SixSigmaCouncil.jpg" },
];

function TrustedPartnersMarquee() {
  return (
    <Box sx={{ bgcolor: "#FAFBFF", position: "relative" }}>
      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <motion.div {...fadeUp(0)}>
          <Box sx={{ textAlign: "center", pt: { xs: 5, md: 6 }, pb: 7 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{
    mb: 2.5,
    width: "100%",
    justifyContent: "center"}}>
              <Sparkles size={13} color="#94A3B8" />
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "#94A3B8", textTransform: "uppercase" }}>
                Trusted training partners
              </Typography>
            </Stack>
            <LogoMarquee />
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}

function AccreditationSection() {
  return (
    <Box sx={{ bgcolor: "#FAFBFF", position: "relative" }}>
      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <motion.div {...fadeUp(0.04)}>
          <Box sx={{ textAlign: "center", pb: 9 }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{
    mb: 2.5,
    width: "100%",
    justifyContent: "center"}}>
              <Sparkles size={13} color="#94A3B8" />
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "#94A3B8", textTransform: "uppercase" }}>
                Accreditations
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: { xs: 12.5, md: 13.5 }, color: "#94A3B8", mb: 4, fontWeight: 500 }}>
              Recognized and endorsed by leading global bodies
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(6, 1fr)" },
                gap: { xs: 1.5, md: 2 },
              }}
            >
              {accreditations.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    height: { xs: 96, md: 108 },
                    px: 2,
                    borderRadius: "14px",
                    bgcolor: "#FFFFFF",
                    border: "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                    transition: "transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 28px -14px rgba(37,99,235,0.35)",
                      borderColor: "rgba(37,99,235,0.28)",
                    },
                  }}
                >
                  <Box component="img" src={item.src} alt={item.name} sx={{ height: { xs: 32, md: 36 }, width: { xs: 32, md: 36 }, objectFit: "contain" }} />
                  <Typography sx={{ fontSize: { xs: 10.5, md: 11.5 }, fontWeight: 600, color: "#64748B", textAlign: "center", lineHeight: 1.2 }}>
                    {item.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}

function PartnersStrip({ variant = "full" }) {
  const compact = variant === "compact";
  const { data, isLoading } = useQuery({
    queryKey: ["partners-featured"],
    queryFn: () => getFeaturedPartners().then((r) => normalizeList(r.data)),
  });
  const list = (data || []).slice(0, compact ? 6 : undefined);

  return (
    <Box sx={{
      borderRadius: "18px", border: "1px solid rgba(15,23,42,0.08)", bgcolor: "white",
      boxShadow: "0 1px 3px rgba(15,23,42,0.05)", p: { xs: 3, md: 4 },
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1.15rem", color: "#0B1530" }}>
            Collaboration with Industry leaders
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.5 }}>
            Strategic OEM &amp; enterprise relationships that power our delivery
          </Typography>
        </Box>
        {compact && (
          <MuiLink
            component={Link}
            to="/business?tab=clients-partners"
            sx={{ fontSize: 13, fontWeight: 700, color: "#2563EB", textDecoration: "none", display: "flex", alignItems: "center", gap: 0.5 }}
          >
            View all <ArrowRight size={14} />
          </MuiLink>
        )}
      </Stack>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={22} /></Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,1fr)", sm: "repeat(3,1fr)", md: `repeat(${compact ? 6 : 4},1fr)` }, gap: 2, justifyContent: "center" }}>
          {list.map((p) => (
            <Box
              key={p.id || p.name}
              sx={{
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                gap: 1, height: 104, px: 2, borderRadius: "12px", bgcolor: "#FAFBFF", border: "1px solid rgba(15,23,42,0.06)",
                transition: "all 0.22s ease",
                "&:hover": { borderColor: "rgba(37,99,235,0.3)", bgcolor: "white", boxShadow: "0 8px 20px -12px rgba(37,99,235,0.3)", transform: "translateY(-2px)" },
              }}
            >
              <BrandLogo src={p.logo || p.image} name={p.name} height={36} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// partner_type values assumed to match your backend enum — adjust if needed.
const partnerCategories = [
  { value: "All", label: "All" },
  { value: "TECHNOLOGY_PARTNER", label: "Technology Partners" },
  { value: "ENTERPRISE_CLIENT", label: "Enterprise Clients" },
  { value: "GOVERNMENT", label: "Government" },
];

function PartnersSection() {
  const [activeCategory, setActiveCategory] = useState("All");

  const { data, isLoading } = useQuery({
    queryKey: ["partners", activeCategory],
    queryFn: () =>
      getPartners(activeCategory === "All" ? {} : { partner_type: activeCategory }).then((r) => normalizeList(r.data)),
  });

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <motion.div {...fadeUp(0)}>
        <Box sx={{ textAlign: "center" }}>
          <Eyebrow>Our clients & partners</Eyebrow>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.7rem", md: "2.1rem" }, color: "#0B1530", letterSpacing: "-0.02em", mb: 1.5 }}>
            Trusted by Industry Leaders
          </Typography>
          <Typography sx={{ fontSize: 15, color: "#475569", lineHeight: 1.75, maxWidth: 640, mx: "auto", mb: 4 }}>
            Long-standing partnerships with global technology leaders and government departments.
          </Typography>
        </Box>
      </motion.div>

      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1, mb: 4 }}>
        {partnerCategories.map((cat) => {
          const isActive = activeCategory === cat.value;
          return (
            <Chip
              key={cat.value}
              label={cat.label}
              onClick={() => setActiveCategory(cat.value)}
              sx={{
                fontWeight: 700, fontSize: 13, borderRadius: "999px", cursor: "pointer",
                transition: "all 0.2s ease",
                bgcolor: isActive ? "#2563EB" : "rgba(37,99,235,0.06)",
                color: isActive ? "white" : "#2563EB",
                "&:hover": { bgcolor: isActive ? "#1D4ED8" : "rgba(37,99,235,0.12)" },
              }}
            />
          );
        })}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} /></Box>
      ) : (!data || data.length === 0) ? (
        <Typography sx={{ color: "text.secondary", fontSize: 14, textAlign: "center" }}>No partners found.</Typography>
      ) : (
        <CardGrid minWidth={240}>
         {data.map((partner, i) => (
           <ArticleCard
              key={partner.id || partner.name}
              image={partner.logo}
              imageFit="contain"
              title={partner.name}
              eyebrowIcon={Building2}
              eyebrowLabel={partner.partner_type?.replace("_", " ")}
              delay={i * 0.04}
              href={`/partners/${partner.id}`}
            />
          ))}
        </CardGrid>
      )}
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero                                                                */
/* ------------------------------------------------------------------ */

function BusinessHeroSection() {
  return (
    <Box sx={{ position: "relative", overflow: "hidden" }}>
      <Box sx={{ position: "relative", pt: { xs: 12, md: 16 }, pb: { xs: 13, md: 17 }, overflow: "hidden" }}>
        {/* Background photograph */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${heroBg})`,
            backgroundSize: "cover",
            backgroundPosition: { xs: "right center", md: "center 30%" },
            transform: { xs: "none", md: "scale(1.04)" },
          }}
        />

        {/* Directional gradient scrim */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(100deg, rgba(6,12,32,0.94) 0%, rgba(8,16,42,0.87) 32%, rgba(11,21,48,0.55) 58%, rgba(11,21,48,0.26) 76%, rgba(11,21,48,0.12) 100%)",
          }}
        />

        {/* Fade into page background at bottom */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
           height: { xs: 90, md: 120 },
            background: "linear-gradient(180deg, rgba(250,251,255,0) 0%, #FAFBFF 100%)",
          }}
        />

        {/* Fine blueprint grid over the scrim side */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "linear-gradient(100deg, black 0%, black 35%, transparent 65%)",
            WebkitMaskImage: "linear-gradient(100deg, black 0%, black 35%, transparent 65%)",
          }}
        />

        {/* Ambient glow */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -120,
            left: "8%",
            width: 640,
            height: 460,
            background: "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(37,99,235,0.32), transparent 70%)",
            pointerEvents: "none",
            filter: "blur(10px)",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Box sx={{ maxWidth: 700 }}>
            <motion.div {...fadeUp(0)}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.75,
                  py: 0.6,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.22)",
                  bgcolor: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(8px)",
                  mb: 2.75,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#5B9BFF" }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.1em", color: "#BFD4FF", textTransform: "uppercase" }}>
                  Business Solutions
                </Typography>
              </Box>
            </motion.div>

            <motion.div {...fadeUp(0.08)}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "1.9rem", sm: "2.4rem", md: "3rem" },
                  lineHeight: 1.12,
                  letterSpacing: "-0.025em",
                  color: "#FFFFFF",
                  textShadow: "0 2px 24px rgba(0,0,0,0.35)",
                }}
              >
                Empower your business with{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #7CB2FF 0%, #A9C9FF 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  cutting-edge IT solutions
                </Box>
              </Typography>
            </motion.div>

            <motion.div {...fadeUp(0.14)}>
              <Typography sx={{ mt: 2.5, fontSize: { xs: 14.5, md: 16 }, lineHeight: 1.75, color: "rgba(226,232,255,0.86)", maxWidth: 560 }}>
                From IT consultancy to hardware and software solutions, NetLabs ITS delivers innovation,
                reliability, and growth for businesses of all sizes.
              </Typography>
            </motion.div>

            {/* Stat strip — glass panel, matches Gateway */}
            <motion.div {...fadeUp(0.2)}>
              <Box
                sx={{
                  display: "inline-flex",
                  flexWrap: "wrap",
                  gap: { xs: 3, md: 5 },
                  rowGap: 2,
                  mt: 4,
                  px: { xs: 2.5, md: 3.5 },
                  py: { xs: 2, md: 2.25 },
                  borderRadius: "16px",
                  border: "1px solid rgba(255,255,255,0.16)",
                  bgcolor: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(14px)",
                }}
              >
                {[
                  { value: "40K+", label: "Clients Served", icon: Users },
                  { value: "180+", label: "Enterprise Partners", icon: Building2 },
                  { value: "60+", label: "IT Solutions", icon: Package },
                ].map((stat) => (
                  <Box key={stat.label} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(255,255,255,0.1)",
                        flexShrink: 0,
                      }}
                    >
                      <stat.icon size={16} color="#A9C9FF" strokeWidth={2} />
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.45rem" }, fontWeight: 800, color: "#FFFFFF", letterSpacing: "-0.02em", lineHeight: 1 }}>
                        {stat.value}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "rgba(226,232,255,0.75)", mt: 0.4, fontWeight: 600 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

const TAB_KEYS = TAB_META.map((t) => t.value);

const BREADCRUMB_LABEL = {
  overview: "Business Overview",
  "it-services": "IT Services & Consultancy",
  "hardware-software": "Hardware & Software Products",
  "clients-partners": "Our Clients & Partners",
};

export default function BusinessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
    const breadcrumbRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && TAB_KEYS.includes(tab)) {
      setActiveTab(tab);
    } else {
      setActiveTab("overview");
      navigate("/business?tab=overview", { replace: true });
    }
  }, [location.search, navigate]);

      const handleTabChange = (value) => {
    navigate(`/business?tab=${value}`);
    breadcrumbRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell>
      <BusinessHeroSection />
       <TrustedPartnersMarquee />
      <AccreditationSection />
      <TabSwitcher active={activeTab} onChange={handleTabChange} />

      <Box sx={{ bgcolor: "#FAFBFF", position: "relative" }}>
        <Container maxWidth="lg" sx={{ position: "relative" }}>
                    <Box sx={{ pt: 3, display: "flex", justifyContent: "center", scrollMarginTop: "140px" }} ref={breadcrumbRef}>
            <Breadcrumbs sx={{ fontSize: 13 }}>
              <MuiLink component={Link} to="/" sx={{ textDecoration: "none", color: "#64748B", fontSize: 13 }}>Home</MuiLink>
              <MuiLink component={Link} to="/business?tab=overview" sx={{ textDecoration: "none", color: "#64748B", fontSize: 13 }}>Business</MuiLink>
              <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 600 }}>{BREADCRUMB_LABEL[activeTab]}</Typography>
            </Breadcrumbs>
          </Box>

          {activeTab === "overview" && <OverviewSection />}
          {activeTab === "it-services" && <ServicesSection />}
          {activeTab === "hardware-software" && <CatalogSection />}
          {activeTab === "clients-partners" && <PartnersSection />}
        </Container>
      </Box>

      <ContactSection />
    </PageShell>
  );
}